import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useGameStore, useLocalBubbleId, useLocalBubbleType, useBubbleNames, useProximityCount, EventBus } from '@campus/core';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { LinkifiedText } from '../components/LinkifiedText.js';
import { Button, Input } from '../ds/index.js';
import { useBubbleChatStore } from '../../stores/bubbleChatStore.js';
import { MeetingChatPanel } from './MeetingChatPanel.js';

/**
 * ProximityChatPanel — discussion de la bulle (ou des personnes à proximité),
 * petite fenêtre en bas à gauche, au-dessus de la barre de contrôle.
 * Visible seulement dans une bulle ou à proximité de quelqu'un.
 *
 * Ouverture : bouton « Bulle » de la barre ou touche T. Échap pour fermer.
 * Les messages restent visibles tant que la fenêtre est ouverte.
 * Comme le panneau latéral (SidePanel), elle ne prend le clavier du jeu
 * (ui:chat-open / ui:chat-close) que pendant la saisie : avatar et carte
 * restent pilotables fenêtre ouverte, un clic sur la carte quitte le champ.
 *
 * Dans une salle de réunion (ordinateur) : chat de la réunion façon Meet,
 * panneau latéral qui reste ouvert (MeetingChatPanel) ; fermé, un message
 * reçu s'affiche en aperçu.
 */
export function ProximityChatPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [text, setText] = useState('');
  const messages = useGameStore((s) => s.proximityMessages);
  const localBubbleId = useLocalBubbleId();
  const proximityCount = useProximityCount();
  const isMobile = useIsMobile();
  const canChat = !!localBubbleId || proximityCount > 0;
  const localBubbleType = useLocalBubbleType();
  const bubbleNames = useBubbleNames();
  const meetingMode = !isMobile && localBubbleType === 'meeting-room' && !!localBubbleId;
  const meetingModeRef = useRef(meetingMode);
  meetingModeRef.current = meetingMode;
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const scrollRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  // Fenêtre fermée pendant la saisie : le champ disparaît sans focusout fiable.
  const typingRef = useRef(false);
  const setTyping = (typing: boolean) => {
    if (typingRef.current === typing) return;
    typingRef.current = typing;
    EventBus.emit(typing ? 'ui:chat-open' : 'ui:chat-close', {});
  };
  useEffect(() => () => setTyping(false), []);

  // Clic hors de la fenêtre (sur la carte) pendant la saisie : on quitte le champ.
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (!typingRef.current || sectionRef.current?.contains(e.target as Node)) return;
      const el = document.activeElement;
      if (el instanceof HTMLElement && sectionRef.current?.contains(el)) el.blur();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, []);

  function close() {
    setIsOpen(false);
    setTyping(false);
  }

  function toggle() {
    const s = useGameStore.getState();
    if (!s.localBubbleId && s.proximityCount === 0) return;
    setIsOpen((v) => {
      if (v) setTyping(false);
      return !v;
    });
  }

  // Ouverte : les messages sont lus (pastille du bouton « Bulle » remise à zéro).
  useEffect(() => {
    useBubbleChatStore.getState().setOpen(isOpen);
  }, [isOpen]);
  useEffect(() => () => useBubbleChatStore.getState().setOpen(false), []);

  // Chat de réunion : un seul panneau latéral à la fois (messagerie, personnes).
  useEffect(() => {
    if (isOpen && meetingMode) EventBus.emit('ui:side-panel-opened', { panel: 'meeting-chat' });
  }, [isOpen, meetingMode]);
  useEffect(() => {
    const onOther = ({ panel }: { panel: string }) => { if (panel !== 'meeting-chat' && meetingModeRef.current) close(); };
    EventBus.on('ui:side-panel-opened', onOther);
    return () => { EventBus.off('ui:side-panel-opened', onOther); };
  }, []);

  // Réunion, chat fermé : aperçu du message reçu (comme Meet).
  useEffect(() => {
    const onMsg = (m: { isSelf?: boolean; displayName: string; text: string; id?: string }) => {
      if (m.isSelf || !m.id || isOpenRef.current || !meetingModeRef.current) return;
      const text = m.text.length > 90 ? `${m.text.slice(0, 90)}…` : m.text;
      EventBus.emit('ui:toast', { message: `${m.displayName} : ${text}`, tone: 'info' });
    };
    EventBus.on('chat:proximity-received', onMsg);
    return () => { EventBus.off('chat:proximity-received', onMsg); };
  }, []);

  // Fermeture automatique en quittant la bulle et la proximité
  useEffect(() => {
    if (!canChat && isOpen) close();
  }, [canChat, isOpen]);

  // Raccourci T (hors saisie)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (useGameStore.getState().adminPage) return; // campus en arrière-plan
      if (e.key === 't' || e.key === 'T') {
        const s = useGameStore.getState();
        if (!s.localBubbleId && s.proximityCount === 0) return;
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Bouton « Bulle » de la barre
  useEffect(() => {
    EventBus.on('ui:toggle-chat-panel', toggle);
    return () => { EventBus.off('ui:toggle-chat-panel', toggle); };
  }, []);

  // Défilement vers le dernier message
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, isOpen]);

  function handleSend(e: FormEvent) {
    e.preventDefault();
    const value = text.trim();
    if (value) EventBus.emit('ui:chat-bubble-send', { text: value });
    setText('');
  }

  // Ouvert sur ordinateur : la pile des invites du bas se centre à côté (ds.css, .pl-stack-bottom).
  useEffect(() => {
    if (!isOpen || isMobile || meetingMode) return;
    const root = document.documentElement;
    root.style.setProperty('--pl-bubble-chat', '336px');
    return () => { root.style.removeProperty('--pl-bubble-chat'); };
  }, [isOpen, isMobile, meetingMode]);

  if (!isOpen) return null;
  if (meetingMode) {
    return <MeetingChatPanel roomName={bubbleNames.get(localBubbleId!) ?? 'Salle de réunion'} onClose={close} />;
  }

  const people = proximityCount + 1;
  const title = `${localBubbleId ? 'Bulle' : 'À proximité'} · ${people} personne${people > 1 ? 's' : ''}`;

  return (
    <section
      ref={sectionRef}
      aria-label="Discussion de la bulle"
      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); close(); } }}
      onFocus={(e) => setTyping(e.target.tagName === 'INPUT')}
      onBlur={(e) => { if (!sectionRef.current?.contains(e.relatedTarget as Node)) setTyping(false); }}
      style={{
        position: 'absolute',
        left: isMobile ? 12 : 'calc(16px + env(safe-area-inset-left, 0px))',
        right: isMobile ? 12 : undefined,
        bottom: 'calc(var(--pl-hud-bottom) + 16px)',
        width: isMobile ? undefined : 320,
        maxHeight: isMobile ? '45%' : 380,
        boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column',
        background: 'var(--color-bg-surface)',
        border: '1px solid var(--color-border-default)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-lg)',
        pointerEvents: 'auto',
        zIndex: 'var(--z-panel)' as unknown as number,
        fontFamily: 'var(--font-body)',
        color: 'var(--color-text-primary)',
        overflow: 'hidden',
      }}
    >
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        padding: '6px 6px 6px 14px', borderBottom: '1px solid var(--color-border-default)',
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 'var(--text-sm)', fontWeight: 700 }}>
          <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--color-accent)' }} />
          {title}
        </span>
        <Button variant="ghost" size="sm" icon="chevron-down" aria-label="Réduire la discussion" onClick={close} />
      </header>

      <div
        ref={scrollRef}
        style={{
          flex: 1, overflowY: 'auto', touchAction: 'pan-y', minHeight: 96,
          padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        {messages.length === 0 ? (
          <p style={{ margin: 'auto 0', textAlign: 'center', fontSize: 'var(--text-label)', color: 'var(--color-text-muted)' }}>
            Personne n'a encore écrit. Lance la discussion !
          </p>
        ) : messages.map((msg, i) => {
          const time = new Date(msg.timestamp);
          const timeStr = `${time.getHours().toString().padStart(2, '0')}:${time.getMinutes().toString().padStart(2, '0')}`;
          return (
            <div key={`${msg.senderId}-${msg.timestamp}-${i}`} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 'var(--text-label)', fontWeight: 700, color: msg.isSelf ? 'var(--color-text-primary)' : 'var(--color-primary-text)' }}>
                  {msg.isSelf ? 'Toi' : msg.displayName}
                </span>
                <span style={{ font: '700 11px/1 var(--font-mono)', color: 'var(--color-text-muted)' }}>{timeStr}</span>
              </div>
              <span style={{ fontSize: 'var(--text-sm)', lineHeight: 1.45, overflowWrap: 'anywhere' }}><LinkifiedText text={msg.text} /></span>
            </div>
          );
        })}
      </div>

      <form onSubmit={handleSend} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', padding: '0 12px 12px' }}>
        <Input
          aria-label="Message à la bulle"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Écrire à la bulle…"
          maxLength={200}
          autoFocus
          className="pl-grow"
        />
        <Button variant="primary" type="submit" style={{ height: 38 }}>Envoyer</Button>
      </form>
    </section>
  );
}
